import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import Page from '@/components/page'
import { PadDashboardSummary, PadInfo, usePadContracts } from '@/features/Pad'
import { useFlag } from '@/flags'
import useBetaUser from '@/hooks/use-beta-user'
import useCurrentSession from '@/hooks/use-current-session'
import {
  DashboardPanel,
  DashboardRow,
  DashboardContent,
  DashboardTitle,
  DashboardDescription
} from '@/components/settings'
import { breakpoints, structure } from '@/styles'
import { toUnit } from '@/utils/styles'
import PageTitle from '@/components/page-title'
const StyledPage = styled(Page)`
  height: calc(100vh - ${toUnit(structure.header.height)});
`
const StyledDashboardRow = styled(DashboardRow)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;
  }
`

const StyledDashboardContent = styled(DashboardContent)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 100%;
    margin-bottom: ${props => props.bottom ?? 24}px;
    text-align: center;
    align-items: center;
  }
`
const StyledDashboardTitle = styled(DashboardTitle)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: unset;
  }
`
const StyledDashboardDescription = styled(DashboardDescription)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin-right: unset;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  max-width: 748px;
  width: 100%;
  margin: 0 auto 80px;
  padding: 0 16px;
  flex: 1;
  @media screen and (max-width: ${breakpoints.desktop}) {
    padding: 0 40px;
  }
`

type DashboardProps = {
  padInfo: PadInfo
}

const PaymentSettingsDashboard: React.FC<DashboardProps> = ({ padInfo }) => {
  const { t } = useTranslation()
  const session = useCurrentSession()
  const betaUser = useBetaUser()
  const betaUserPad = betaUser.pad
  const currentBetaUser = betaUserPad.includes(parseInt(session.id))
  const showPad = useFlag(['features', 'pad'], false) && currentBetaUser
  const { contracts: padContracts } = usePadContracts()
  return (
    <StyledPage>
      <ContentWrapper>
        <PageTitle>{t('l.settings.view.payment_settings.title', 'Payment Settings')}</PageTitle>{' '}
        {showPad && (
          <>
            <AreaTitle>
              {t(
                'r.payment_settings.view.pad_section.title',
                'Set up pre-authorized debits to make rental payments'
              )}
            </AreaTitle>
            <PadDashboardSummary padContracts={padContracts} padInfo={padInfo} />
          </>
        )}
        <AreaTitle>
          {t('r.payment_settings.view.billing_address.title', 'Billing address')}
        </AreaTitle>
        <DashboardPanel>
          <StyledDashboardRow>
            <StyledDashboardContent>
              <StyledDashboardTitle>
                {t('r.payment_settings.view.billing_address.label', 'Billing Address')}
              </StyledDashboardTitle>
              <StyledDashboardDescription>
                {t('r.payment_settings.view.billing_address.text', 'Manage your billing address')}
              </StyledDashboardDescription>
            </StyledDashboardContent>
            <Button theme="primary" to="/settings/payment-settings/billing-address">
              {t('r.payment_settings.view.view.button', 'View')}
            </Button>
          </StyledDashboardRow>
        </DashboardPanel>
      </ContentWrapper>
    </StyledPage>
  )
}

export default PaymentSettingsDashboard
